'use client';
//@ts-ignore
import '@vidstack/react/player/styles/default/theme.css';
//@ts-ignore
import '@vidstack/react/player/styles/default/layouts/audio.css';
//@ts-ignore
import '@vidstack/react/player/styles/default/layouts/video.css';
import * as React from 'react';
import ReactDOM from 'react-dom';
import Image from 'next/image';
import {
  MediaPlayer,
  MediaPlayerInstance,
  MediaProvider,
  PlayButton,
  Poster,
  useMediaState,
} from '@vidstack/react';
import {
  defaultLayoutIcons,
  DefaultVideoLayout,
} from '@vidstack/react/player/layouts/default';
import Hls from 'hls.js';
import { useEffect, useMemo } from 'react';
import { stegaClean } from '@sanity/client/stega';
import { IMuxVideoPlayer } from '@/sanity/globals/VideoPlayer';

interface HLSVideoPlayerProps extends React.ComponentPropsWithRef<'video'> {
  source: IMuxVideoPlayer;
}

export const HLSVideoPlayer = React.forwardRef<
  HTMLVideoElement,
  HLSVideoPlayerProps
>(function HLSVideoPlayer(
  props: HLSVideoPlayerProps,
  ref: React.ForwardedRef<HTMLVideoElement>,
) {
  const videoRef = React.useRef<HTMLVideoElement>();
  const hlsRef = React.useRef<Hls>();
  const { source, ...rest } = props;
  const cleanedVideoSrc = useMemo(() => {
    return stegaClean(source?.muxVideo?.playbackId ?? '');
  }, [source]);

  useEffect(() => {
    if (Hls.isSupported() && source?.muxVideo?.playbackId && videoRef.current) {
      hlsRef.current = new Hls({
        fetchSetup: function (context, initParams: RequestInit) {
          initParams.mode = 'no-cors';
          return new Request(context.url, initParams);
        },
      });
      hlsRef.current.loadSource(
        `https://stream.mux.com/${cleanedVideoSrc}.m3u8?redundant_streams=true`,
      );
      hlsRef.current.attachMedia(videoRef.current);
    }

    return () => {
      if (hlsRef.current && videoRef.current) {
        hlsRef.current.detachMedia();
        hlsRef.current.destroy();
      }
    };
  }, [cleanedVideoSrc, source]);

  return (
    <video
      ref={(node) => {
        if (node) {
          videoRef.current = node;
        }

        if (ref) {
          if (typeof ref === 'function') {
            ref(node);
          } else {
            ref.current = node;
          }
        }
      }}
      {...rest}
      crossOrigin="anonymous"
    />
  );
});

// Shows only when paused — hides itself once video starts
const CustomPlayButton = () => {
  const isPaused = useMediaState('paused');

  if (!isPaused) return null;

  return (
    <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
      <PlayButton
        className="flex items-center justify-center group pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <Image
          src="/global/video-play-btn.png"
          alt="Play"
          width={80}
          height={80}
          className="w-[10rem] h-[10rem] transition-transform duration-500 ease-out group-hover:scale-[1.06]"
        />
      </PlayButton>
    </div>
  );
};

function getOptimizedImage(url?: string, q = 75) {
  if (!url || url.startsWith('data:')) return undefined;
  return `/_next/image?url=${encodeURIComponent(url)}&w=1280&q=${q}`;
}

export interface VideoPlayerProps extends React.HTMLAttributes<MediaPlayerInstance> {
  playerData: IMuxVideoPlayer;
  playsinline?: boolean;
  disableClickToToggle?: boolean;
  /** Hides the centered play button for callers that render their own (replay hero). */
  hideBigPlayButton?: boolean;
}

/** Same rule the player uses to pick its source: embed URL when `embed`, else the Mux playback id. */
export function hasPlayableSource(playerData?: IMuxVideoPlayer | null) {
  if (!playerData) return false;
  return playerData.embed ? !!stegaClean(playerData.embedUrl ?? '') : !!playerData.muxVideo?.playbackId;
}

const SanityVideoPlayer = React.forwardRef<
  MediaPlayerInstance,
  VideoPlayerProps
>(
  (
    {
      playerData,
      className,
      playsinline = false,
      disableClickToToggle = false,
      hideBigPlayButton = false,
      ...props
    },
    ref,
  ) => {
    const cleanedVideoSrc = useMemo(() => {
      return playerData ? stegaClean(playerData) : playerData;
    }, [playerData]);

    const isEmbed = !!playerData?.embed;
    const cleanedPlaybackId = (cleanedVideoSrc as IMuxVideoPlayer)?.muxVideo?.playbackId;
    const cleanedPosterSrc = useMemo(() => {
      const custom = (cleanedVideoSrc as IMuxVideoPlayer)?.thumbnail?.src || undefined;
      if (custom) return custom;
      if (playerData?.embed && playerData?.embedUrl) {
        const url = stegaClean(playerData.embedUrl ?? '');
        const match = url.match(/(?:youtube\.com\/embed\/|youtu\.be\/|youtube\.com\/watch\?v=)([^?&/]+)/);
        if (match) return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`;
      }
      if (cleanedPlaybackId) return `https://image.mux.com/${cleanedPlaybackId}/thumbnail.jpg`;
      // Vidstack renders <img src=""> when poster is empty — use 1px blank as fallback
      return 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    }, [cleanedVideoSrc, playerData, cleanedPlaybackId]);

    const videoSrc = useMemo(() => {
      if (!playerData?.embed) {
        return cleanedPlaybackId
          ? `https://stream.mux.com/${cleanedPlaybackId}.m3u8?redundant_streams=true`
          : undefined;
      }
      const url = stegaClean(playerData.embedUrl ?? '');
      if (!url) return undefined;
      // Vidstack needs watch URL format — convert embed URLs
      const match = url.match(/youtube\.com\/embed\/([^?&/]+)/);
      if (match) return `https://www.youtube.com/watch?v=${match[1]}`;
      return url;
    }, [playerData, cleanedPlaybackId]);

    const playerRef = React.useRef<MediaPlayerInstance | null>(null) as React.MutableRefObject<MediaPlayerInstance | null>;
    const [playerInstance, setPlayerInstance] = React.useState<MediaPlayerInstance | null>(null);

    React.useImperativeHandle(ref, () => playerInstance as MediaPlayerInstance, [playerInstance]);

    const handleClick = () => {
      if (disableClickToToggle) return;
      if (playerRef.current) {
        if (playerRef.current.paused) {
          playerRef.current.play();
        } else {
          playerRef.current.pause();
        }
      }
    };

    if (!videoSrc) return null;

    const optimizedPoster = getOptimizedImage(cleanedPosterSrc);
    if (optimizedPoster) ReactDOM.preload(optimizedPoster, { as: 'image' } as any);

    return (
      <div className={disableClickToToggle ? 'w-full h-full' : 'w-full h-full cursor-pointer'}>
        <MediaPlayer
          ref={(el) => {
            playerRef.current = el;
            setPlayerInstance(el);
          }}
          className={className}
          src={videoSrc || undefined}
          poster={getOptimizedImage(cleanedPosterSrc)}
          load="visible"
          playsInline={playsinline}
          onClick={isEmbed ? undefined : handleClick}
          // @ts-ignore
          onPlay={props?.onPlay}
          // @ts-ignore
          onPause={props?.onPause}
          // @ts-ignore
          onCanPlay={props?.onCanPlay}
        >
          <MediaProvider />
          {cleanedPosterSrc && (
            <Poster
              className="vds-poster !w-full !h-full object-cover !rounded-[inherit]"
              src={getOptimizedImage(cleanedPosterSrc)}
              alt=""
            />
          )}
          {!hideBigPlayButton && <CustomPlayButton />}
          <DefaultVideoLayout
            className="!rounded-[inherit] overflow-clip block absolute top-0 start-0 w-full h-full"
            icons={defaultLayoutIcons}
            noScrubGesture={false}
            slots={{ loadButton: <></> }}
            thumbnails={
              cleanedPlaybackId
                ? `https://image.mux.com/${cleanedPlaybackId}/storyboard.vtt`
                : undefined
            }
          />
        </MediaPlayer>
      </div>
    );
  },
);

SanityVideoPlayer.displayName = 'SanityVideoPlayer';
export { SanityVideoPlayer };
